import COS from 'cos-js-sdk-v5'
import { Response, toHump } from './utils'
import {
  UploadTempPermit, UploadResponse, GetToken,
} from './interface'

export class Uploader {
  // 上传业务，用于和 scene 指定上传云服务的 bucket，需业务方从媒体中台申请
  bizName: string

  // 上传场景，用于和 bizName 指定上传云服务的 bucket，需业务方从媒体中台申请
  scene: string

  // 获取上传 token 的异步请求
  getToken: GetToken

  enableResume?: boolean

  constructor(
    args: {
      bizName: string
      scene: string
      getToken: GetToken
      enableResume?: boolean
    },
  ) {
    this.bizName = args.bizName
    this.scene = args.scene
    this.getToken = args.getToken
    this.enableResume = args.enableResume
  }

  private useCache = false

  get cacheKey(): string {
    return `uploader-permit-${this.scene}-${this.bizName}`
  }

  private getCachePermit(file: File | Blob): UploadTempPermit | null {
    if (!(file instanceof File)) return null
    try {
      const fileKey = `${file.name}-${file.size}`
      const cacheStr = localStorage.getItem(this.cacheKey)
      if (!cacheStr) return null
      const cacheObj = JSON.parse(cacheStr)
      const tempPermit = cacheObj[fileKey]
      if (!tempPermit) return null
      if (tempPermit.expireTime - 2 * 60 * 1000 < Date.now()) { // 过期(2min buffer)
        this.setCachePermit(file, null)
        return null
      }
      return tempPermit
    } catch (e) {
      localStorage.setItem(this.cacheKey, '')
      return null
    }
  }

  private setCachePermit(file: File | Blob, permit: UploadTempPermit | null): void {
    if (!(file instanceof File)) return
    try {
      const fileKey = `${file.name}-${file.size}`
      const cacheStr = localStorage.getItem(this.cacheKey)
      const cacheObj = cacheStr ? JSON.parse(cacheStr) : {}
      cacheObj[fileKey] = permit
      localStorage.setItem(this.cacheKey, JSON.stringify(cacheObj))
    } catch (e) {
      // do nothing
    }
  }

  private getPermit(
    file: File | Blob,
    fileFormat?: string,
    persistentOps?: string,
    persistentNotifyUrl?: string,
  ): Promise<UploadTempPermit> {
    if (this.enableResume) {
      const cachePermit = this.getCachePermit(file)
      if (cachePermit) {
        this.useCache = true
        return Promise.resolve(cachePermit)
      }
    }
    this.useCache = false
    return this.getToken(
      {
        bizName: this.bizName,
        scene: this.scene,
        fileCount: 1,
        fileFormat,
        persistentOps,
        persistentNotifyUrl,
      },
    )
      .then(
        ({ uploadTempPermits, uploadLimitPolicy }) => {
          const fileType = file.type
          const fileSize = file.size

          if (uploadLimitPolicy) {
            const { fileTypes, maxSize } = uploadLimitPolicy

            if (
              fileTypes?.length
              && !fileTypes
                .reduce(
                  (a: string[], c) => [
                    ...a,
                    ...c.split('/'),
                  ],
                  [],
                )
                .some(
                  f => fileType.endsWith(f),
                )
            ) {
              throw new Error(`文件类型 ${fileType} 不在可上传的文件类型 ${fileTypes.join(', ')} 中。`)
            }

            if (maxSize !== undefined && fileSize > maxSize) {
              throw new Error(`文件尺寸（${fileSize / (1024 * 1024)} mb）超过最大尺寸（${maxSize / (1024 * 1024)} mb）。`)
            }
          }

          if (!uploadTempPermits?.length) {
            throw new Error('token 获取失败。')
          }
          const [permit] = uploadTempPermits.sort((a, b) => b.qos - a.qos)

          // set cache
          if (this.enableResume) {
            this.setCachePermit(file, permit)
          }
          return permit
        },
      )
  }

  post(
    options: Omit<COS.UploadFileParams, 'Bucket' | 'Region' | 'Key'> & {
      Body: File | Blob
      fileFormat?: string
      onInstanceCreated?: (instance: COS) => void
    },
  ): Promise<Response<UploadResponse>> {
    const startTime = Date.now()
    const { fileFormat, onInstanceCreated, ...rest } = options
    const res = new Response<UploadResponse>(
      {
        info: {
          fileType: options.Body.type,
          fileSize: options.Body.size,
          startTime,
        },
      },
    )

    return this.getPermit(options.Body, fileFormat)
      .then(
        permit => {
          res.setInfo({
            ...res.info,
            tokenEndTime: Date.now(),
          })
          const [fileId] = permit.fileIds
          const cosInitParams: COS.COSOptions = {
            Domain: permit.uploadAddr,
            getAuthorization(_, callback) {
              callback({
                TmpSecretId: permit.secretId || 'null',
                TmpSecretKey: permit.secretKey || 'null',
                SecurityToken: permit.token,
                StartTime: Math.floor(startTime / 1000),
                ExpiredTime: Math.floor(permit.expireTime / 1000),
              })
            },
          }
          if (permit.bucket === 'unknown') {
            // @ts-ignore 特殊参数，自建通道时没有 bucket
            cosInitParams.AppId = '1251524319'
          }

          const cos = new COS(cosInitParams)

          if (onInstanceCreated) {
            onInstanceCreated(cos)
          }

          return cos
            .uploadFile(
              {
                Bucket: permit.bucket,
                Region: permit.region,
                Key: fileId,
                ...rest,
              },
            )
            .then(
              resp => {
                const payload: UploadResponse = {
                  ...resp,
                  bizName: this.bizName,
                  scene: this.scene,
                  cloudType: permit.cloudType,
                  fileId,
                  useCache: this.useCache,
                }

                if (permit.cdnDomain) {
                  const cdnLink = `${permit.cdnDomain}/${fileId}`
                  payload.cdnDomain = permit.cdnDomain
                  payload.url = cdnLink
                  payload.previewUrl = cdnLink
                  payload.staticUrl = cdnLink
                }

                // 返回所有自定义 header（ 'x-ros' 自检服务约定）
                if (resp?.headers) {
                  Object.keys(resp.headers).forEach(v => {
                    if (v.startsWith('x-ros-')) {
                      const key = toHump(v.split('x-ros-')[1])
                      // @ts-ignore
                      payload[key] = resp.headers?.[v]
                    }
                  })
                }

                const metrics = {
                  fileId,
                  cloudType: permit.cloudType,
                  uploaderAddr: permit.uploadAddr,
                  sliceSize: rest.SliceSize || 1048576,
                  isSlice: options.Body.size > (rest.SliceSize || 1048576),
                  tokenFail: false,
                }

                // @ts-ignore
                if (resp?.headers?.['x-tracer']) metrics.tracerId = resp.headers?.['x-tracer']

                // 上传成功，清除token缓存
                this.setCachePermit(options.Body, null)

                return res
                  .setData(payload)
                  .log(this, metrics)
              },
            )
            .catch(
              (e: COS.CosError) => {
                // 非网络问题类型的失败，清除token缓存
                if (this.enableResume && !e?.message?.includes('network error')) {
                  this.setCachePermit(options.Body, null)
                }
                return res
                  .fail()
                  .setCode(Number(e?.code || -1))
                  .setMsg(e?.message)
                  .log(this, {
                    fileId,
                    cloudType: permit.cloudType,
                    uploaderAddr: permit.uploadAddr,
                    sliceSize: rest.SliceSize || 1048576,
                    isSlice: options.Body.size > (rest.SliceSize || 1048576),
                    tokenFail: false,
                    serverFail: true,
                  })
              },
            )
        },
      )
      .catch(
        (e: Error) => res
          .fail()
          .setInfo({
            ...res.info,
            tokenEndTime: Date.now(),
          })
          .setMsg(e.message)
          .log(this, {
            tokenFail: true,
          }),

      )
  }
}
